"use client";

/**
 * fund-score-radar.tsx
 *
 * WTM Fund Score radar chart with sub-score progress bars. Wraps the existing
 * src/components/shared/radar-chart.tsx component (already pure SVG) and
 * adds a horizontal progress-bar list for the 6 sub-scores on the right.
 *
 * Used in: src/app/funds/[slug]/page.tsx (WTM Assessment section — PRO tier)
 */

import { RadarChart } from "@/components/shared/radar-chart";

export interface FundScoreRadarSubScores {
  costEfficiency?: number | null;
  performanceConsistency?: number | null;
  riskManagement?: number | null;
  fundManagerQuality?: number | null;
  portfolioQuality?: number | null;
  transparency?: number | null;
}

export interface FundScoreRadarProps {
  overallScore: number | null;
  subScores: FundScoreRadarSubScores | null;
}

const SUB_SCORE_META: Array<{
  key: keyof FundScoreRadarSubScores;
  label: string;
}> = [
  { key: "costEfficiency", label: "Cost Efficiency" },
  { key: "performanceConsistency", label: "Performance" },
  { key: "riskManagement", label: "Risk Management" },
  { key: "fundManagerQuality", label: "Fund Manager" },
  { key: "portfolioQuality", label: "Portfolio Quality" },
  { key: "transparency", label: "Transparency" },
];

function scoreColor(score: number): string {
  if (score >= 9) return "#10B981"; // green
  if (score >= 6) return "#3B82F6"; // blue
  if (score >= 4) return "#F59E0B"; // amber
  return "#EF4444"; // red
}

function starsFor(score: number): string {
  // Map 1.0–5.0 overall score to filled stars out of 5
  const full = Math.round(score);
  return "★".repeat(full) + "☆".repeat(5 - full);
}

export function FundScoreRadar({ overallScore, subScores }: FundScoreRadarProps) {
  if (!subScores || overallScore === null || overallScore === undefined) {
    return (
      <div className="bg-surface border border-surface-light/10 rounded-xl p-5">
        <p className="text-muted-foreground text-sm">
          WTM Assessment not available for this report.
        </p>
      </div>
    );
  }

  const axes = SUB_SCORE_META.map((m) => ({
    label: m.label,
    value: subScores[m.key] ?? 0,
  }));

  return (
    <div className="bg-surface border border-surface-light/10 rounded-xl p-5">
      <div className="flex flex-col lg:flex-row items-center gap-6">
        {/* Radar + overall score */}
        <div className="relative shrink-0">
          <RadarChart axes={axes} />
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-foreground text-4xl font-bold font-tabular leading-none">
              {overallScore.toFixed(1)}
            </span>
            <span
              className="text-lg mt-1 tracking-wider"
              style={{ color: "#F59E0B" }}
            >
              {starsFor(overallScore)}
            </span>
          </div>
        </div>

        {/* Sub-score progress bars */}
        <div className="flex-1 w-full space-y-3">
          {SUB_SCORE_META.map((m) => {
            const score = subScores[m.key] ?? 0;
            const color = scoreColor(score);
            return (
              <div key={m.key}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-muted-foreground text-xs">
                    {m.label}
                  </span>
                  <span
                    className="text-xs font-bold font-tabular"
                    style={{ color }}
                  >
                    {score}/10
                  </span>
                </div>
                <div className="h-1 rounded-full bg-surface-light/10 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                      width: `${(score / 10) * 100}%`,
                      backgroundColor: color,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
